import { Circle, Defs, Image, Line, LinearGradient, Path, Rect, Stop, Svg, Text as TextPdf, View } from '@react-pdf/renderer'
import React from 'react'
import { AssessmentResponse, SigmaScores } from '../..'

// Os tipos do @react-pdf não declaram fontSize/fontFamily no Text dentro de Svg, mas o renderer os suporta
const Text = TextPdf as unknown as React.FC<React.ComponentProps<typeof TextPdf> & { fontSize?: number, fontFamily?: string }>

interface iProps {
  combinado: AssessmentResponse
  imagemPerfilPi?: string
}

const Azul = '#0093D0'
const Escuro = '#222222'
const Apagado = '#CCCCCC'

const Fatores = [
  { letra: 'A', esquerda: 'Colaborativo', direita: 'Dominante' },
  { letra: 'B', esquerda: 'Reservado', direita: 'Extrovertido' },
  { letra: 'C', esquerda: 'Impaciente', direita: 'Paciente' },
  { letra: 'D', esquerda: 'Informal', direita: 'Formal' },
  { letra: 'E', esquerda: 'Subjetivo', direita: 'Objetivo' }
]

const Largura = 709
const MargemEsquerda = 15
const MargemDireita = 694
const Zero = 356
const PorSigma = 114
const PassoLinha = 25.5
const PassoBloco = 195

function ObterValores(scores?: SigmaScores): Array<number | undefined> {
  return [scores?.aSigma, scores?.bSigma, scores?.cSigma, scores?.dSigma, scores?.eSigma]
}

function LadoAtivo(valor: number | undefined, lado: 'esquerda' | 'direita'): boolean {
  if (valor == null || (valor >= -0.2 && valor <= 0.2)) return true
  return lado === 'esquerda' ? valor < 0 : valor > 0
}

function PosicaoX(valor: number): number {
  return Zero + Math.max(-3, Math.min(3, valor)) * PorSigma
}

function Bloco(titulo: string, scores: SigmaScores | undefined, y0: number, comE: boolean): JSX.Element {
  const valores = ObterValores(scores)
  const linhas = comE ? 5 : 4
  const yLinha = (i: number): number => y0 + 65 + i * PassoLinha
  const topoBarra = yLinha(linhas - 1) + 18
  const pontos = valores.slice(0, linhas).map((v, i) => v == null ? null : { x: PosicaoX(v), y: yLinha(i) })

  return (
    <>
      <Text x={MargemEsquerda} y={y0 + 23} fill={Azul} fontSize={19} fontFamily='Helvetica-Bold'>{titulo}</Text>
      {[-3, -2, -1, 0, 1, 2, 3].map(k => (
        <Text
          key={k}
          x={k === -3 ? MargemEsquerda : k === 3 ? MargemDireita : Zero + k * PorSigma}
          y={y0 + 38}
          fill={Apagado}
          fontSize={11}
          fontFamily='Roboto'
          textAnchor={k === -3 ? 'start' : k === 3 ? 'end' : 'middle'}
        >{k === 0 ? '0' : `${k > 0 ? '+' : '-'}${Math.abs(k)}σ`}</Text>
      ))}
      <Line x1={MargemEsquerda} y1={y0 + 43} x2={MargemDireita} y2={y0 + 43} stroke={Apagado} strokeWidth={3} />
      {[-2, -1, 0, 1, 2].map(k => (
        <Line key={k} x1={Zero + k * PorSigma} y1={y0 + 47} x2={Zero + k * PorSigma} y2={topoBarra} stroke='#E3E3E3' strokeWidth={1} />
      ))}
      {Fatores.slice(0, linhas).map((f, i) => (
        <React.Fragment key={f.letra}>
          <Text x={MargemEsquerda} y={yLinha(i) + 4.5} fill={LadoAtivo(valores[i], 'esquerda') ? Escuro : Apagado} fontSize={14} fontFamily='Helvetica'>{f.esquerda}</Text>
          <Text x={MargemDireita} y={yLinha(i) + 4.5} fill={LadoAtivo(valores[i], 'direita') ? Escuro : Apagado} fontSize={14} fontFamily='Helvetica' textAnchor='end'>{f.direita}</Text>
        </React.Fragment>
      ))}
      <Rect x={MargemEsquerda} y={topoBarra} width={MargemDireita - MargemEsquerda} height={12} fill='url(#barraPI)' />
      <Text x={MargemEsquerda + 4} y={topoBarra + 9} fill='#FFFFFF' fontSize={7} fontFamily='Helvetica-Bold'>BAIXO</Text>
      <Text x={MargemDireita - 4} y={topoBarra + 9} fill='#FFFFFF' fontSize={7} fontFamily='Helvetica-Bold' textAnchor='end'>ALTO</Text>
      <Path d={`M ${Zero} ${topoBarra + 2} L ${Zero + 6} ${topoBarra + 12} L ${Zero - 6} ${topoBarra + 12} Z`} fill={Escuro} />
      {scores?.mScore != null && <Text x={MargemEsquerda} y={topoBarra + 26} fill={Azul} fontSize={11} fontFamily='Helvetica'>{`M: ${scores.mScore}`}</Text>}
      {pontos.slice(1, 4).map((p, i) => {
        const anterior = pontos[i]
        if (!p || !anterior) return null
        return <Line key={i} x1={anterior.x} y1={anterior.y} x2={p.x} y2={p.y} stroke='#555555' strokeWidth={1.3} />
      })}
      {pontos.map((p, i) => p && (
        <React.Fragment key={i}>
          <Circle cx={p.x} cy={p.y} r={11} fill='#FFFFFF' stroke={Azul} strokeWidth={1.6} />
          <Text x={p.x} y={p.y + 4.3} fill={Azul} fontSize={12} fontFamily='Helvetica-Bold' textAnchor='middle'>{Fatores[i].letra}</Text>
        </React.Fragment>
      ))}
    </>
  )
}

function GradienteBarra(): JSX.Element {
  return (
    <Defs>
      <LinearGradient id='barraPI' x1='0' y1='0' x2='1' y2='0'>
        <Stop offset='0' stopColor='#B7E3F6' />
        <Stop offset='1' stopColor='#5BB9E2' />
      </LinearGradient>
    </Defs>
  )
}

export const PerfilPISelf: React.FC<{ scores: SigmaScores, width: number }> = ({ scores, width }) => {
  const altura = scores.mScore != null ? 190 : 175

  return (
    <Svg width={width} height={width * altura / Largura} viewBox={`0 0 ${Largura} ${altura}`}>
      {GradienteBarra()}
      {Bloco('Self', scores, 0, false)}
    </Svg>
  )
}

export const PerfilPIGraficos: React.FC<iProps> = ({ combinado, imagemPerfilPi }) => {
  const comE = combinado.synthesisSigmaScores?.eSigma != null
  const altura = 2 * PassoBloco + (comE ? 215 : 190)

  return (
    <View style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 384, backgroundColor: '#ffffff', width: 310 }}>
      {imagemPerfilPi
        ? <Image src={imagemPerfilPi} style={{ width: 290, objectFit: 'contain' }} />
        : (
          <Svg width={290} height={290 * altura / Largura} viewBox={`0 0 ${Largura} ${altura}`}>
            {GradienteBarra()}
            {Bloco('Self', combinado.selfSigmaScores, 0, false)}
            {Bloco('Conceito do Self', combinado.selfConceptSigmaScores, PassoBloco, false)}
            {Bloco('Síntese', combinado.synthesisSigmaScores, 2 * PassoBloco, comE)}
          </Svg>
          )}
    </View>
  )
}
